import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import {
  Layout, notification, Space, Button, Tooltip, Table, Radio, Row, Col, Alert, Spin
} from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  RetweetOutlined,
  ReadOutlined,
  InfoCircleOutlined,
  SoundOutlined,
  LinkOutlined
} from '@ant-design/icons';
import constants from '../constants/constants';
import ThumbnailPlaceholder from '../assets/thumbnail-placeholder.png';

const { Header, Footer } = Layout;

const BirdDetails = () => {
  const { id } = useParams();
  const [bird, setBird] = useState(null);
  const waveformRef = useRef(null);
  const waveSurferRef = useRef(null);
  const [isLooping, setIsLooping] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedTrack, setSelectedTrack] = useState(0);
  const [redirectFailed, setRedirectFailed] = useState(false);

  const tracks = bird?.audio?.src
    ? [{
      key: 0,
      track_name: bird.audio.track_name || 'Unknown Track',
      credits: bird.audio.credits || 'Unknown',
      src: bird.audio.src
    }]
    : [];

    useEffect(() => {
      if (!waveformRef.current || tracks.length === 0) return;
    
      // Destroy previous instance if exists
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy();
      }
    
      waveSurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#00aaff',
        progressColor: '#0077cc',
        responsive: true,
        height: 80,
      });
    
      loadTrack(selectedTrack);
    
      waveSurferRef.current.on('ready', () => {
        setDuration(waveSurferRef.current.getDuration());
      });
    
      waveSurferRef.current.on('audioprocess', () => {
        setCurrentTime(waveSurferRef.current.getCurrentTime());
      });
    
      waveSurferRef.current.on('finish', () => {
        if (isLooping) waveSurferRef.current.play();
        else setIsPlaying(false);
      });
    
      return () => {
        waveSurferRef.current?.destroy();
      };
    }, [selectedTrack, isLooping, tracks.length]);
    

  const loadTrack = (trackKey) => {
    waveSurferRef.current.load(tracks[trackKey].src);
  };

  const toggleLoop = () => setIsLooping((prevState) => !prevState);

  const togglePlayPause = () => {
    waveSurferRef.current.playPause();
    setIsPlaying(waveSurferRef.current.isPlaying());
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return <span>{minutes}:{seconds}</span>;
  };

  const handleRowSelection = (e) => {
    setSelectedTrack(e.target.value);
  };

  const columns = [
    {
      title: 'Select',
      dataIndex: 'key',
      width: '10%',
      render: (key) => (
        <Radio.Group
          value={selectedTrack}
          onChange={handleRowSelection}
        >
          <Radio value={key} />
        </Radio.Group>
      ),
    },
    {
      title: 'Track Name',
      dataIndex: 'track_name',
      width: '90%',
      ellipsis: true,
      render: (track_name, record) => (
        <div>
          <Space>
            {record.key === selectedTrack && <SoundOutlined className="text-blue-500" />}
            {track_name}
          </Space>
          <div className="text-xs">© {record.credits}</div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      notification.warning({
        message: constants.AudioDisclaimerHeader,
        description: constants.AudioDisclaimer,
        placement: 'topRight',
        duration: 25,
      });
    }, 60000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetch(`${constants.ApiUrl}/get-birds-details/${id}`)
      .then(async (res) => {
        if (res.status === 400) {
          // If server returns 400, trigger page not found
          setRedirectFailed(true);
          return null;
        }
        if (!res.ok) {
          // Handle other errors
          throw new Error('Network response error');
        }
        const data = await res.json();
        return data;
      })
      .then((data) => {
        if (data?.data) {
          setBird(data.data); // render the bird details
        } else if (!redirectFailed) {
          setRedirectFailed(true); // fallback if data is missing
        }
      })
      .catch(() => setRedirectFailed(true));
  }, [id]);
  

  useEffect(() => {
    if (bird?.redirect_url) {
      const timer = setTimeout(() => {
        setRedirectFailed(true);
      }, 5000);

      window.location.href = bird.redirect_url;

      return () => clearTimeout(timer);
    }
  }, [bird?.redirect_url]);

  if (redirectFailed) {
    return <Navigate to="*" replace />;
  }

  // LOADING fallback for null bird
  if (!bird) {
    return (
      <Spin tip="Loading bird..." size="large" fullscreen />
    );
  }

  if (bird?.redirect_url && !redirectFailed) {
    return (
      <Spin tip="Redirecting..." size="large" fullscreen />
    );
  }

  if (bird?.redirect_url && redirectFailed) {
    return (
      <div>
        <Header className='!px-4 !bg-slate-200'>
          <Link to="/" className="!text-black font-bold block w-full">
            {constants.BookName}
          </Link>
        </Header>
        <div className='min-h-[calc(100vh-101px)] container mx-auto px-3 text-center lg:grid lg:place-content-center'>
          <div className="py-5">
            <h1 className="font-bold capitalize text-xl">{bird.common_name}</h1>
            <h2 className="italic text-gray-400 font-normal capitalize">{bird.scientific_name}</h2>
            <Space direction='vertical' size={'large'} className='!w-full'>
              <h1 className='text-lg lg:text-xl font-semibold !mb-0'>
                {constants.RedirectHeader}
              </h1>
              <div>
                <a href={bird.redirect_url} className='text-lg underline underline-offset-4 text-blue-500 capitalize'>
                  <LinkOutlined /> {bird.common_name} {constants.PlaybackLink}
                </a>
              </div>
              <div className='text-left lg:w-11/12 mx-auto'>
                <Alert message={constants.RedirectInfo} type="info" showIcon />
              </div>
            </Space>
          </div>
        </div>
        <Footer className="text-center !py-2">
          © {new Date().getFullYear()} {constants.FooterCred}
        </Footer>
      </div>
    );
  }

  return (
    <div>
      <Header className='!px-4 !bg-slate-200'>
        <Link to="/" className="!text-black font-bold block w-full">
          {constants.BookName}
        </Link>
      </Header>
      <div className='min-h-[calc(100vh-101px)] container mx-auto px-3 text-center lg:grid lg:place-content-center'>
        <Suspense
          fallback={
            <Spin tip="Loading page..." size="large" fullscreen />
          }
        >
          <div className="py-5">
            <Row gutter={{ xs: 0, lg: 8 }}>
              <Col xs={24} lg={5}>
                <div className="relative">
                  <img
                    src={bird.thumbnail || ThumbnailPlaceholder}
                    alt={bird.common_name}
                    className="!w-11/12 md:!w-1/3 lg:!w-11/12 mx-auto rounded-lg shadow-lg"
                  />
                  {bird.image_credits && (
                    <span className="absolute bottom-3 right-5">
                      <Tooltip title={bird.image_credits}>
                        <Button shape="circle" size="small" icon={<InfoCircleOutlined />} />
                      </Tooltip>
                    </span>
                  )}
                </div>
                <div className="my-3">
                  <h1 className="font-bold capitalize text-xl">{bird.common_name}</h1>
                  <h2 className="italic text-gray-400 font-normal capitalize">{bird.scientific_name}</h2>
                </div>
              </Col>
              <Col xs={24} lg={19}>
                {tracks.length > 0 && (
                  <div>
                    <div ref={waveformRef} />
                    <div className="flex justify-between text-xs">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>

                    <Table
                      dataSource={tracks}
                      columns={columns}
                      pagination={false}
                      rowKey="key"
                      size="small"
                      showHeader={false}
                      className="my-4 cursor-pointer"
                      rowClassName={(record) =>
                        record.key === selectedTrack ? 'bg-blue-100' : ''
                      }
                      onRow={(record) => ({
                        onClick: () => setSelectedTrack(record.key),
                      })}
                    />

                    <div>
                      <Space className="gap-x-5">
                        <Tooltip title={constants.BuyBook}>
                          <Button
                            shape="circle"
                            className="!min-w-12 !w-12 !h-12"
                            icon={<ReadOutlined className="!text-2xl" />}
                          />
                        </Tooltip>

                        <Tooltip title={isPlaying ? constants.Pause : constants.Play}>
                          <Button
                            shape="circle"
                            className="!min-w-[70px] !w-[70px] !h-[70px]"
                            onClick={togglePlayPause}
                            type={isPlaying ? 'primary' : ''}
                            icon={isPlaying
                              ? <PauseCircleOutlined className="!text-5xl" />
                              : <PlayCircleOutlined className="!text-5xl" />}
                          />
                        </Tooltip>

                        <Tooltip title={isLooping ? constants.DisableLoop : constants.EnableLoop}>
                          <Button
                            shape="circle"
                            className="!min-w-12 !w-12 !h-12"
                            onClick={toggleLoop}
                            type={isLooping ? 'primary' : ''}
                            icon={<RetweetOutlined className="!text-2xl" />}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Suspense>
      </div>
      <Footer className="text-center !py-2">
        © {new Date().getFullYear()} {constants.FooterCred}
      </Footer>
    </div>
  );
};

export default BirdDetails;
